// 非同期Knowledge Base API対応のサービス（Cognito認証統合版）
import { appConfig } from '../config/env';
import { 
  AsyncKnowledgeBaseRequest,
  AsyncKnowledgeBaseStartResponse,
  AsyncKnowledgeBaseStatusResponse,
  AsyncKnowledgeBaseCompletedResponse,
  AsyncKnowledgeBaseFailedResponse,
  AsyncStatus,
  BedrockError,
  ApiResult,
  KnowledgeBaseResponse,
  Citation
} from '../types/ApiTypes';

// 非同期処理用のモックレスポンス（Markdown形式対応）
const MOCK_ASYNC_RESPONSES = [
  {
    base: `# RAGアプリケーションについて

**RAG（Retrieval-Augmented Generation）** は、検索拡張生成技術の革新的なアプローチです。

## 主な特徴

- **高精度な回答生成**: 大規模な知識ベースから関連情報を検索
- **最新情報の活用**: リアルタイムでドキュメントを参照
- **引用機能**: 回答の根拠となる情報源を明示

### 技術スタック

\`\`\`typescript
// 使用技術の例
const techStack = {
  frontend: "React + TypeScript",
  backend: "AWS Bedrock + Lambda",
  database: "Aurora PostgreSQL + pgvector",
  ai: "Claude Sonnet 4"
};
\`\`\`

> このプロジェクトでは、最新のAI技術を活用して高品質なRAGシステムを構築しています。`,
    citations: [
      {
        content: `## RAG技術の概要

**RAG（Retrieval-Augmented Generation）** は、検索拡張生成技術で、以下の特徴があります：

- **高精度検索**: 大規模な知識ベースから関連情報を検索
- **動的生成**: 検索結果を基にAIが回答を生成
- **最新情報**: リアルタイムでドキュメントを参照

### 従来手法との比較

| 項目 | 従来のAI | RAG |
|------|----------|-----|
| 情報源 | 学習データのみ | 外部知識ベース |
| 更新性 | 再学習が必要 | リアルタイム更新 |
| 精度 | 限定的 | 高精度 |

> RAGにより、より正確で最新の情報に基づいた回答が可能になります。`,
        metadata: { source: "rag-detailed-overview.md", section: "技術概要" }
      },
      {
        content: `### システム構成

このプロジェクトでは、以下の技術スタックを組み合わせています：

#### バックエンド
- **Amazon Bedrock Knowledge Base** - AI推論エンジン
- **Aurora PostgreSQL Serverless v2** - スケーラブルデータベース
- **pgvector** - ベクトル検索拡張

#### 処理フロー
1. \`ベクトル検索\` により関連ドキュメントを高速検索
2. \`Claude Sonnet 4\` で自然な回答を生成
3. \`引用情報\` と共に結果を返却

\`\`\`typescript
// 検索処理の例
const searchResult = await vectorSearch(query);
const response = await claudeGenerate(searchResult);
\`\`\``,
        metadata: { source: "project-architecture-detailed.md", section: "システム構成" }
      }
    ]
  },
  {
    base: `## AWS Bedrockの非同期処理機能

Amazon Bedrockは、**複数の基盤モデル**を統一APIで利用できるマネージドサービスです。

### サポートモデル

| モデル | 特徴 | 用途 |
|--------|------|------|
| Claude | 高精度な推論 | 複雑な質問応答 |
| Llama | オープンソース | カスタマイズ重視 |
| Titan | AWS純正 | 企業向け |

#### 非同期処理のメリット

1. **長時間処理対応**: 複雑な推論タスクも効率的に処理
2. **リソース最適化**: サーバーリソースの有効活用
3. **スケーラビリティ**: 大量のリクエストに対応

\`\`\`bash
# AWS CLI での非同期処理例
aws bedrock-runtime invoke-model-async \\
  --model-id anthropic.claude-v2 \\
  --body file://request.json \\
  --output-location s3://my-bucket/output/
\`\`\``,
    citations: [
      {
        content: `## Amazon Bedrock サービス概要

**Amazon Bedrock** は、複数の基盤モデルを統一APIで利用できるマネージドサービスです。

### サポートモデル一覧

- **Claude** (Anthropic) - 高精度な推論・対話
- **Llama** (Meta) - オープンソースモデル
- **Titan** (Amazon) - AWS純正モデル
- **Jurassic** (AI21 Labs) - 多言語対応

### 非同期処理の利点

> 非同期処理により、長時間の推論タスクも効率的に処理できます。

#### 主な機能
1. **バッチ処理** - 大量データの一括処理
2. **リソース最適化** - コスト効率の向上
3. **スケーラビリティ** - 需要に応じた自動スケール

\`\`\`json
{
  "model": "anthropic.claude-v2",
  "async": true,
  "maxTokens": 4000
}
\`\`\``,
        metadata: { source: "aws-bedrock-advanced.md", section: "非同期処理" }
      }
    ]
  },
  {
    base: `# プロジェクト構成とアーキテクチャ

このRAGアプリケーションは、**モダンなフルスタック構成**で構築されています。

## フロントエンド

- **React 19.1.0** - 最新のReactフレームワーク
- **TypeScript 5.8.3** - 型安全性の確保
- **Markdown対応** - リッチなコンテンツ表示

### コンポーネント構成

\`\`\`
src/
├── components/
│   ├── ChatInterface.tsx
│   ├── MessageList.tsx
│   ├── MarkdownRenderer.tsx  # 新機能！
│   └── CitationDisplay.tsx
├── services/
│   └── chatService.ts
└── types/
    └── Message.ts
\`\`\`

## バックエンド

- **AWS Lambda** - サーバーレス実行環境
- **Amazon Bedrock** - AI推論エンジン
- **Aurora PostgreSQL** - ベクトルデータベース

### API エンドポイント

- \`POST /knowledge-base\` - RAG検索・生成
- \`GET /async-status\` - 非同期処理状況確認

---

**注意**: このシステムは開発中のため、仕様が変更される可能性があります。`,
    citations: [
      {
        content: `## アーキテクチャ詳細

### フロントエンド技術

**React + TypeScript** で構築された現代的なSPAです。

#### 主要機能
- ✅ **Markdownレンダリング** - 豊富な表現が可能
- ✅ **リアルタイムチャット** - WebSocket対応
- ✅ **レスポンシブデザイン** - モバイル最適化
- ✅ **引用表示** - 情報源の明示

### バックエンド構成

**サーバーレス構成** で高い可用性を実現：

\`\`\`mermaid
graph LR
    A[React App] --> B[API Gateway]
    B --> C[Lambda Function]
    C --> D[Bedrock]
    C --> E[Aurora PostgreSQL]
\`\`\`

#### 使用サービス
- **AWS Lambda** - サーバーレス実行環境
- **Amazon Bedrock** - AI推論エンジン  
- **Aurora PostgreSQL** - ベクトルデータベース
- **API Gateway** - RESTful API

> サーバーレス構成により、運用コストを最小化しながら高いスケーラビリティを実現しています。`,
        metadata: { source: "architecture-overview.md", section: "システム構成" }
      }
    ]
  }
];

// 実際の非同期Knowledge Base API呼び出し関数（処理開始、Cognito認証版）
const startAsyncKnowledgeBaseApi = async (
  query: string,
  idToken: string,
  maxResults?: number, 
  sessionId?: string
): Promise<AsyncKnowledgeBaseStartResponse> => {
  const requestBody: AsyncKnowledgeBaseRequest = {
    query,
    ...(maxResults && { max_results: maxResults }),
    ...(sessionId && { session_id: sessionId })
  };

  const apiUrl = `${appConfig.knowledgeBaseApiUrl}${appConfig.asyncKnowledgeBaseEndpoint}`;

  if (appConfig.debugMode) {
    console.log(`[DEBUG] Starting Async Knowledge Base API (Cognito): ${apiUrl}`);
    console.log(`[DEBUG] Request:`, requestBody);
    console.log(`[DEBUG] Using ID Token:`, idToken ? 'Yes' : 'No');
  }

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorData: BedrockError;
    
    try {
      errorData = JSON.parse(errorText);
    } catch {
      errorData = {
        error: `HTTP ${response.status}`,
        message: errorText || 'Unknown error occurred',
        status_code: response.status
      };
    }
    
    throw new Error(`Async Knowledge Base API Error (${response.status}): ${errorData.message || errorData.error}`);
  }

  const data: AsyncKnowledgeBaseStartResponse = await response.json();
  
  if (appConfig.debugMode) {
    console.log(`[DEBUG] Async Start Response:`, data);
  }

  return data;
};

// 実際の非同期処理状況確認API呼び出し関数（Cognito認証版）
const checkAsyncKnowledgeBaseStatus = async (
  requestId: string,
  idToken: string
): Promise<AsyncKnowledgeBaseStatusResponse> => {
  const apiUrl = `${appConfig.knowledgeBaseApiUrl}${appConfig.asyncStatusEndpoint}?requestId=${requestId}`;

  if (appConfig.debugMode) {
    console.log(`[DEBUG] Checking Async Status (Cognito): ${apiUrl}`);
    console.log(`[DEBUG] Request ID: ${requestId}`);
  }

  const response = await fetch(apiUrl, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorData: BedrockError;
    
    try {
      errorData = JSON.parse(errorText);
    } catch {
      errorData = {
        error: `HTTP ${response.status}`,
        message: errorText || 'Unknown error occurred',
        status_code: response.status
      };
    }
    
    throw new Error(`Async Status API Error (${response.status}): ${errorData.message || errorData.error}`);
  }

  const data: AsyncKnowledgeBaseStatusResponse = await response.json();
  
  if (appConfig.debugMode) {
    console.log(`[DEBUG] Async Status Response:`, data);
  }

  return data;
};

// モック非同期処理開始関数
const startMockAsyncKnowledgeBase = async (
  query: string, 
  maxResults?: number, 
  sessionId?: string
): Promise<AsyncKnowledgeBaseStartResponse> => {
  await new Promise(resolve => setTimeout(resolve, appConfig.mockDelayMs));
  
  const mockRequestId = `mock-async-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  return {
    requestId: mockRequestId,
    status: AsyncStatus.PROCESSING,
    message: `非同期処理を開始しました: ${query}`,
    estimatedCompletionTime: new Date(Date.now() + 30000).toISOString() // 30秒後
  };
};

// モック非同期処理状況確認関数
const checkMockAsyncStatus = async (requestId: string): Promise<AsyncKnowledgeBaseStatusResponse> => {
  await new Promise(resolve => setTimeout(resolve, 500)); // 短い遅延
  
  // リクエストIDから経過時間を推定（モック用）
  const timestampMatch = requestId.match(/mock-async-(\d+)/);
  const startTime = timestampMatch ? parseInt(timestampMatch[1]) : Date.now() - 60000;
  const elapsedTime = Date.now() - startTime;
  
  // 30秒経過で完了とする
  if (elapsedTime > 30000) {
    const randomResponse = MOCK_ASYNC_RESPONSES[
      Math.floor(Math.random() * MOCK_ASYNC_RESPONSES.length)
    ];

    // モック用の引用情報を生成
    const mockCitations: Citation[] = randomResponse.citations.map((citation, index) => ({
      generated_response_part: {
        textResponsePart: {
          span: {
            start: index * 100,
            end: (index + 1) * 100
          },
          text: randomResponse.base.substring(index * 100, (index + 1) * 100)
        }
      },
      retrieved_references: [{
        content: citation.content,
        metadata: citation.metadata
      }]
    }));

    const completedResponse: AsyncKnowledgeBaseCompletedResponse = {
      requestId,
      status: AsyncStatus.COMPLETED,
      message: '非同期処理が完了しました',
      response: { // result → response に変更
        query: `非同期処理クエリ (${requestId})`,
        generated_response: `${randomResponse.base} 非同期処理により詳細な分析を行い、包括的な回答を生成しました。`,
        citations: mockCitations,
        knowledge_base_id: 'MOCK_ASYNC_KB_ID',
        model_arn: 'mock:async:model:arn',
        session_id: `mock-async-session-${Date.now()}`,
        guardrail_action: 'NONE'
      },
      completedAt: new Date().toISOString(),
      processingTimeMs: elapsedTime
    };

    return completedResponse;
  } else {
    // まだ処理中
    const progress = Math.min(Math.floor((elapsedTime / 30000) * 100), 95);
    return {
      requestId,
      status: AsyncStatus.PROCESSING,
      message: `処理中... (${progress}% 完了)`,
      estimatedCompletionTime: new Date(startTime + 30000).toISOString(),
      progress
    };
  }
};

// === 公開API関数 ===

// 非同期処理開始（Cognito認証対応）
export const startAsyncKnowledgeBase = async (
  query: string,
  idToken?: string,
  maxResults?: number, 
  sessionId?: string
): Promise<AsyncKnowledgeBaseStartResponse> => {
  if (appConfig.debugMode) {
    console.log(`[DEBUG] Starting async Knowledge Base query: ${query}`);
    console.log(`[DEBUG] Max results: ${maxResults || appConfig.defaultMaxResults}`);
    console.log(`[DEBUG] Session ID: ${sessionId || 'new session'}`);
    console.log(`[DEBUG] Using mock API: ${appConfig.useMockApi}`);
    console.log(`[DEBUG] Has ID Token: ${idToken ? 'Yes' : 'No'}`);
  }

  const effectiveMaxResults = maxResults || appConfig.defaultMaxResults;

  if (appConfig.useMockApi) {
    return await startMockAsyncKnowledgeBase(query, effectiveMaxResults, sessionId);
  } else {
    if (!idToken) {
      throw new Error('Cognito ID Token is required for API access');
    }
    return await startAsyncKnowledgeBaseApi(query, idToken, effectiveMaxResults, sessionId);
  }
};

// 非同期処理状況確認（Cognito認証対応）
export const checkAsyncStatus = async (
  requestId: string,
  idToken?: string
): Promise<AsyncKnowledgeBaseStatusResponse> => {
  if (appConfig.debugMode) {
    console.log(`[DEBUG] Checking async status for request: ${requestId}`);
    console.log(`[DEBUG] Using mock API: ${appConfig.useMockApi}`);
    console.log(`[DEBUG] Has ID Token: ${idToken ? 'Yes' : 'No'}`);
  }

  if (appConfig.useMockApi) {
    return await checkMockAsyncStatus(requestId);
  } else {
    if (!idToken) {
      throw new Error('Cognito ID Token is required for API access');
    }
    return await checkAsyncKnowledgeBaseStatus(requestId, idToken);
  }
};

// エラーハンドリング付き非同期処理開始
export const startAsyncKnowledgeBaseWithError = async (
  query: string,
  idToken?: string,
  maxResults?: number, 
  sessionId?: string
): Promise<ApiResult<AsyncKnowledgeBaseStartResponse>> => {
  if (appConfig.debugMode) {
    console.log(`[DEBUG] Starting async Knowledge Base with error handling: ${query}`);
  }

  try {
    const response = await startAsyncKnowledgeBase(query, idToken, maxResults, sessionId);
    
    return {
      success: true,
      data: response
    };
  } catch (error) {
    if (appConfig.debugMode) {
      console.error(`[DEBUG] Async Knowledge Base Start Error:`, error);
    }
    
    return {
      success: false,
      error: {
        error: error instanceof Error ? error.name : 'Unknown Error',
        message: error instanceof Error ? error.message : 'An unknown error occurred'
      }
    };
  }
};

// エラーハンドリング付き状況確認
export const checkAsyncStatusWithError = async (
  requestId: string,
  idToken?: string
): Promise<ApiResult<AsyncKnowledgeBaseStatusResponse>> => {
  if (appConfig.debugMode) {
    console.log(`[DEBUG] Checking async status with error handling: ${requestId}`);
  }

  try {
    const response = await checkAsyncStatus(requestId, idToken);
    
    return {
      success: true,
      data: response
    };
  } catch (error) {
    if (appConfig.debugMode) {
      console.error(`[DEBUG] Async Status Check Error:`, error);
    }
    
    return {
      success: false,
      error: {
        error: error instanceof Error ? error.name : 'Unknown Error',
        message: error instanceof Error ? error.message : 'An unknown error occurred'
      }
    };
  }
};

// 非同期処理の完了まで待機する関数
export const waitForAsyncCompletion = async (
  requestId: string,
  idToken?: string,
  maxWaitTimeMs: number = 300000, // 5分
  pollIntervalMs: number = 2000 // 2秒
): Promise<KnowledgeBaseResponse> => {
  const startTime = Date.now();
  
  while (Date.now() - startTime < maxWaitTimeMs) {
    const statusResult = await checkAsyncStatusWithError(requestId, idToken);
    
    if (!statusResult.success) {
      throw new Error(`Status check failed: ${statusResult.error.message}`);
    }
    
    const status = statusResult.data;
    
    if (status.status === AsyncStatus.COMPLETED) {
      const completedResponse = status as AsyncKnowledgeBaseCompletedResponse;
      if (completedResponse.response) {
        return completedResponse.response;
      } else {
        throw new Error('Completed response missing response data');
      }
    } else if (status.status === AsyncStatus.FAILED) {
      const failedResponse = status as AsyncKnowledgeBaseFailedResponse;
      throw new Error(`Async processing failed: ${failedResponse.error || failedResponse.message}`);
    }
    
    // まだ処理中の場合は待機
    await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
  }
  
  throw new Error(`Async processing timeout after ${maxWaitTimeMs}ms`);
};

// 非同期処理の簡易実行関数（開始から完了まで）
export const executeAsyncKnowledgeBase = async (
  query: string,
  idToken?: string,
  maxResults?: number,
  sessionId?: string,
  maxWaitTimeMs?: number
): Promise<KnowledgeBaseResponse> => {
  if (appConfig.debugMode) {
    console.log(`[DEBUG] Executing complete async Knowledge Base query: ${query}`);
  }

  // 非同期処理開始
  const startResult = await startAsyncKnowledgeBaseWithError(query, idToken, maxResults, sessionId);
  
  if (!startResult.success) {
    throw new Error(`Failed to start async processing: ${startResult.error.message}`);
  }
  
  // 完了まで待機
  return await waitForAsyncCompletion(startResult.data.requestId, idToken, maxWaitTimeMs);
};
